<template lang="pug">
  .panel-page-white
    .panel-white.body-1.mx-auto
      FormErrorAlert(v-if="message" :message="message" @dismiss="message = ''")
      PageHeader Geo-Activity Report
        button.btn.btn-light(slot="extra" @click="clearForm") Clear Form
      p.mt-3
        | The Geo-Activity Report allows you to run a proactive search to view activity going on in your area. For example, determine individuals who have
        | been blocked in your county or city or who have purchased 2 or more boxes in the past 2 weeks.
      form(
        @submit.prevent="search"
        autocomplete="off"
      )
        .form-row.mt-3
          .col
            h5.section-header DATE RANGE
        div.section
          p Date range has a maximum of 31 days.
          .form-row
            .col-4
              .form-group
                label(for="from-date") From
                Datepicker(
                  input-id="from-date"
                  :input-class="{'form-control': true, 'is-invalid': fromDateInvalid}"
                  v-model="fromDate"
                )
            .col-4
              .form-group
                label(for="to-date") To
                Datepicker(
                  input-id="to-date"
                  :input-class="{'form-control': true, 'is-invalid': toDateInvalid}"
                  v-model="toDate"
                )
        .form-row.mt-3
          .col
            h5.section-header PURCHASER'S ADDRESS
        div.section
          p Do you want to enter a purchaser's address? (City, State, Zip)
          .form-row
            .col
              CustomRadio(v-model="purchaserAddress" :model-value="true") Yes
          .form-row
            .col
              CustomRadio(v-model="purchaserAddress" :model-value="false") No
          .form-row.mt-3(v-show="purchaserAddress")
            .col
              .form-group
                label(for="city") City
                input#city.form-control(v-model="purchaserCity")
            .col
              .form-group
                label(for="state") State
                CustomSelect(
                  v-model="purchaserState"
                  :options="states"
                  labelName="name"
                  valueName="abbreviation")
            .col
              .form-group
                label(for="zip") Zip
                input#zip.form-control(v-model="purchaserZip")
        .form-row.mt-3
          .col
            h5.section-header LOCATION
        div.section
          p Select the state(s) you wish to include in your report.
          .form-row
            .col-8
              .form-group
                label(for="states") State(s)
                SuperSelect(
                  taggable
                  :options="filterStates"
                  label-name="name"
                  value-name="abbreviation"
                  v-model="selectedStates"
                  @filter="stateFilter = $event"
                  @input="getCounties"
                )
        div.section
          .form-row.mt-3(v-for="state in selectedStates")
            .col
              .form-row
                .col
                  p What counties do you want to include within <strong>{{state.name}}</strong>?
              .form-row(:class="{'mb-3': allCounties[state.abbreviation]}")
                .col
                  CustomRadio(
                    :model-value="true"
                    :value="allCounties[state.abbreviation]"
                    @input="clearCounties(state)") All Counties
                  br
                  CustomRadio(
                    :model-value="false"
                    :value="allCounties[state.abbreviation]"
                    @input="$set(allCounties, state.abbreviation, false)") Specific Counties
              .form-row(v-show="!allCounties[state.abbreviation]")
                .col-8
                  .form-group
                    label(:for="state.abbreviation + '-counties'")
                    SuperSelect(
                      taggable
                      :options="filteredCounties[state.abbreviation]"
                      :value="selectedCounties[state.abbreviation]"
                      @input="setCounties(state.abbreviation, $event)"
                      labelName="description"
                      @filter="filterCounties(state.abbreviation, $event)"
                    )
              .form-row(v-for="county in selectedCounties[state.abbreviation]")
                .col
                  .form-row
                    .col
                      p Do you want to select specific stores within <strong>{{county.description}}</strong>?
                  .form-row(:class="{'mb-3': allStores[county.fipsCd]}")
                    .col
                      CustomRadio(
                        :model-value="false"
                        :value="allStores[county.fipsCd]"
                        @input="$set(allStores, county.fipsCd, false)") Yes
                      br
                      CustomRadio(
                        :model-value="true"
                        :value="allStores[county.fipsCd]"
                        @input="$set(allStores, county.fipsCd, true)") No
                  .form-row(v-if="!allStores[county.fipsCd]")
                    .col-8
                      .form-group
                        label(:for="county.fipsCd + '-stores'")
                        SuperSelect(
                          taggable
                          :options="filteredStores[county.fipsCd]"
                          :value="selectedStores[county.fipsCd]"
                          @input="$set(selectedStores, county.fipsCd, $event)"
                          label-name="pharmacyName"
                          value-name="pharmacySid"
                          @filter="filterStores(county.fipsCd, $event)"
                        )
        .form-row.mt-3
          .col
            h5.section-header REPORT OPTIONS
        div.section
          .form-row.mt-3
            .col
              CustomRadio(
                model-value="none"
                :value="activityOption"
                @input="activityOption = $event"
              ) No Activity Specified
          .form-row.mt-3
            .col
              .d-flex
                div.mr-2
                  CustomRadio(
                    model-value="minActivities"
                    :value="activityOption"
                    @input="activityOption = $event"
                  ) Minimum Number of Activities:
                div
                  input.form-control.num-select(
                    v-model="minActivities"
                    :disabled="activityOption !== 'minActivities'")
          .form-row.mt-3
            .col
              .d-flex
                div.mr-2
                  CustomRadio(
                    model-value="minTotalGrams"
                    :value="activityOption"
                    @input="activityOption = $event"
                  ) Minimum Total Activity Grams:
                div
                  input.form-control.num-select(
                    v-model="minTotalGrams"
                    :disabled="activityOption !== 'minTotalGrams'")
        .form-row.mt-3
          .col
            h5.section-header RECORDS
        div.section
          .form-row.mt-3
            .col
              CustomCheckbox(
                :value="recordTypes.includes('RETURNS')"
                @input="toggleRecord('RETURNS')"
              ) Return
          .form-row.mt-2
            .col
              CustomCheckbox(
                :value="recordTypes.includes('PURCHASES')"
                @input="toggleRecord('PURCHASES')"
              ) Purchase
          .form-row.mt-2
            .col
              CustomCheckbox(
                :value="recordTypes.includes('EXCEEDANCES')"
                @input="toggleRecord('EXCEEDANCES')"
              ) Exceedance
          .form-row.mt-2
            .col
              CustomCheckbox(
                :value="recordTypes.includes('BLOCKS')"
                @input="toggleRecord('BLOCKS')"
              ) Block
        button.btn.btn-primary(type="submit") Submit
</template>

<script>
import { saveItem, loadForm } from '@/lib/formstore'
import { subDays, format, differenceInCalendarDays, parse } from 'date-fns/esm'
import { toggle, globalSuccess, globalError, waitReport } from '@/lib/utils'
import axios from '@/lib/axios'
import CustomCheckbox from '@/components/CustomCheckbox'
import CustomRadio from '@/components/CustomRadio'
import CustomSelect from '@/components/CustomSelect'
import Datepicker from '@/components/Datepicker'
import FormHeader from '@/components/FormHeader'
import PageHeader from '@/components/PageHeader'
import states from '../lib/states'
import store from '@/store'
import SuperSelect from '@/components/SuperSelect'

function homeStates() {
  return states.filter(st =>
    store.state.account.states.includes(st.abbreviation),
  )
}

export default {
  name: 'GeoActivitySearch',
  data: function() {
    let homestates = [...homeStates()]
    let allcounties = {}
    homestates.forEach(hs => (allcounties[hs.abbreviation] = true))
    return {
      states,
      message: '',
      formSubmitted: false,
      fromDate: format(subDays(new Date(), 30), 'MM/dd/yyyy'),
      toDate: format(new Date(), 'MM/dd/yyyy'),
      purchaserAddress: false,
      purchaserCity: '',
      purchaserState: '',
      purchaserZip: '',
      selectedStates: homestates,
      stateFilter: '',
      counties: {},
      selectedCounties: {},
      allCounties: allcounties,
      filteredCounties: {},
      stores: {},
      selectedStores: {},
      allStores: {},
      filteredStores: {},
      recordTypes: ['RETURNS', 'PURCHASES', 'EXCEEDANCES', 'BLOCKS'],
      activityOption: 'none',
      minActivities: 0,
      minTotalGrams: 0.0,
    }
  },
  mounted() {
    loadForm(
      'geoactivity',
      'fromDate',
      'toDate',
      'purchaserAddress',
      'purchaserCity',
      'purchaserState',
      'purchaserZip',
      'selectedStates',
      'counties',
      'selectedCounties',
      'allCounties',
      'filteredCounties',
      'stores',
      'selectedStores',
      'allStores',
      'filteredStores',
      'recordTypes',
      'activityOption',
      'minActivities',
      'minTotalGrams',
    ).bind(this)()
    scrollTo(0, 0)
    if (this.selectedStates.length > 0) this.getCounties(this.selectedStates)
  },
  created() {
    // This is a little hacky, but according to https://jira.sdlc.appriss.com/browse/KEYSTONE-1536
    // we need to set the user state to the home state. So we watch the state and wait for the user's
    // states to be set, then set it to the selected states.
    this.$watch(
      () => store.state.account.states,
      () => {
        if (this.selectedStates.length === 0) {
          this.selectedStates = homeStates()
          this.getCounties(this.selectedStates)
        }
      },
    )
  },
  methods: {
    search: function() {
      this.formSubmitted = true
      if (this.dateDifference > 31) {
        this.message = 'From date and To date must be within 31 days.'
        scrollTo(0, 0)
        return
      }

      if (this.formInvalid) {
        this.message =
          'Please select purchaser address, location or record type.'
        scrollTo(0, 0)
        return
      }
      saveItem('geoactivity', 'searchCriteria', this.searchCriteria)
      axios()
        .post('/api/geo_activity_report', this.searchCriteria)
        .then(resp => {
          let id = resp.data.id
          waitReport(id, {name: 'geo_activity_results', params: {id}})
        })
        .catch(err => {
          globalError(err)
        })
      scrollTo(0, 0)
    },
    clearForm: function() {
      this.fromDate = ''
      this.toDate = ''
      this.message = ''
      this.formSubmitted = false
      this.purchaserAddress = false
      this.purchaserCity = ''
      this.purchaserState = ''
      this.purchaserZip = ''
      this.selectedStates = []
      this.counties = {}
      this.selectedCounties = {}
      this.allCounties = {}
      this.filteredCounties = {}
      this.stores = {}
      this.selectedStores = {}
      this.allStores = {}
      this.filteredStores = {}
      this.recordTypes = []
      this.activityOption = 'none'
      this.minActivities = 0
      this.minTotalGrams = 0
    },
    getCounties(states) {
      for (let { abbreviation } of states) {
        axios()
          .get('/api/misc/counties', {
            params: { state: abbreviation },
          })
          .then(resp => {
            this.$set(this.counties, abbreviation, resp.data)
            this.$set(this.filteredCounties, abbreviation, resp.data)
          })

        // Set the country selector to "All Counties" for only the first time.
        if (!this.allCounties.hasOwnProperty(abbreviation))
          this.$set(this.allCounties, abbreviation, true)
      }
    },
    setCounties(abbr, counties) {
      this.$set(this.$set(this.selectedCounties, abbr, counties))
      this.getStores(counties)
    },
    clearCounties(state) {
      this.$set(this.allCounties, state.abbreviation, true)
      let selectedFips = this.selectedCounties[state.abbreviation].map(
        o => o.fipsCd,
      )
      // Make sure we remove all of the stores selected for all of the counties in this state
      selectedFips.forEach(fips => (this.selectedStores[fips] = []))
      this.selectedCounties[state.abbreviation] = []
    },
    getStores(counties) {
      for (let { fipsCd } of counties) {
        axios()
          .get('/api/misc/stores', {
            params: { fips: fipsCd },
          })
          .then(resp => {
            let stores = resp.data
            stores.forEach(st => {
              if (st.inactive) st.pharmacyName = `${st.pharmacyName} - Inactive`
            })
            this.$set(this.stores, fipsCd, stores)
            this.$set(this.filteredStores, fipsCd, stores)
          })
        if (!this.allStores.hasOwnProperty(fipsCd))
          this.$set(this.allStores, fipsCd, true)
      }
    },
    filterCounties(abb, filterText) {
      let all = this.counties[abb]
      this.$set(
        this.filteredCounties,
        abb,
        all.filter(co =>
          co.description.toLowerCase().includes(filterText.toLowerCase()),
        ),
      )
    },
    filterStores(fips, filterText) {
      let all = this.stores[fips]
      this.$set(
        this.filteredStores,
        fips,
        all.filter(store =>
          store.pharmacyName.toLowerCase().includes(filterText.toLowerCase()),
        ),
      )
    },
    toggleRecord(recName) {
      toggle(this.recordTypes, recName)
    },
  },
  computed: {
    formInvalid() {
      let sc = this.searchCriteria
      return (
        this.formSubmitted &&
        (!sc.purchaserCity || !sc.purchaserState || !sc.purchaserZip) &&
        !sc.states &&
        !sc.recordTypes
      )
    },
    jsFromDate() {
      return parse(this.fromDate, 'MM/dd/yyyy', new Date())
    },
    jsToDate() {
      return parse(this.toDate, 'MM/dd/yyyy', new Date())
    },
    dateDifference() {
      return Math.abs(differenceInCalendarDays(this.jsToDate, this.jsFromDate))
    },
    fromDateInvalid() {
      return this.formSubmitted && this.dateDifference > 31
    },
    toDateInvalid() {
      return this.formSubmitted && this.dateDifference > 31
    },
    filterStates() {
      return states.filter(st =>
        st.name.toLowerCase().includes(this.stateFilter.toLowerCase()),
      )
    },
    selectedCountiesForSelectedStates() {
      // For every selected state that doesn't have "All Counties" selected, return the counties.
      let states = this.selectedStates.map(s => s.abbreviation)
      let counties = []
      states.forEach(st => {
        if (!this.allCounties[st] && this.selectedCounties[st])
          counties = [...counties, ...this.selectedCounties[st]]
      })
      counties = counties.filter(c => this.allStores[c.fipsCd])
      return counties
    },
    selectedStoresForSelectedCounties() {
      // For every selected store where the state doesn't have "All Counties" selected
      // and the county doesn't have "All Stores" selected, return in an array
      // let counties = this.flatCounties.map(c => c.fipsCd)
      let stores = []
      this.flatCounties.forEach(c => {
        if (
          !this.allStores[c.fipsCd] &&
          !this.allCounties[c.stateCd] &&
          this.selectedStates.map(st => st.abbreviation).includes(c.stateCd) &&
          this.selectedStores[c.fipsCd]
        )
          stores = [...stores, ...this.selectedStores[c.fipsCd]]
      })
      return stores
    },
    flatCounties() {
      // Flatten out the county listing and also mix the state cd into the county info for use
      // later down the pipeline
      let flattened = []
      Object.entries(this.selectedCounties).forEach(([stateCd, counties]) => {
        counties.forEach(county => {
          flattened.push({ ...county, stateCd })
        })
      })
      return flattened
    },
    // These are the actual final search criteria sent to the API
    searchCriteria() {
      let ret = {}
      if (this.selectedStates.length > 0)
        ret.states = this.selectedStates
          .filter(st => this.allCounties[st.abbreviation])
          .map(st => st.abbreviation)
      if (this.selectedCountiesForSelectedStates.length > 0)
        ret.counties = this.selectedCountiesForSelectedStates.map(c => c.fipsCd)
      if (this.selectedStoresForSelectedCounties.length > 0) {
        ret.stores = this.selectedStoresForSelectedCounties.map(
          st => st.pharmacySid,
        )
      }
      if (this.activityOption === 'minActivities')
        ret.minActivities = this.minActivities
      if (this.activityOption === 'minTotalGrams')
        ret.minTotalGrams = this.minTotalGrams
      if (this.purchaserAddress && this.purchaserCity.length > 0)
        ret.purchaserCity = this.purchaserCity
      if (this.purchaserAddress && this.purchaserState.length > 0)
        ret.purchaserState = this.purchaserState
      if (this.purchaserAddress && this.purchaserZip.length > 0)
        ret.purchaserZip = this.purchaserZip
      let attrs = ['fromDate', 'toDate', 'recordTypes']
      attrs.forEach(attr => {
        if (this[attr] && this[attr].length > 0) ret[attr] = this[attr]
      })
      return ret
    },
  },
  components: {
    PageHeader,
    CustomRadio,
    CustomCheckbox,
    Datepicker,
    CustomSelect,
    FormHeader,
    SuperSelect,
  },
}
</script>

<style lang="scss" scoped>
.section-header {
  border-bottom: 1px solid #e6e6e6;
  padding-bottom: 0.6rem;
}

.section {
  background-color: #f8f8f8;
  padding: 1rem;
}

.num-select {
  width: 5rem;
}
</style>